'use client'

import * as React from 'react'
import { useNavigate } from 'react-router-dom'
import { onAuthStateChanged, signOut } from 'firebase/auth'
import { collection, getDocs, query, where } from 'firebase/firestore'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'components/ui/card'
import { Avatar, AvatarFallback } from 'components/ui/avatar'
import { Badge } from 'components/ui/badge'
import { Button } from 'components/ui/button'
import { Input } from 'components/ui/input'
import { Separator } from 'components/ui/separator'
import { toast } from 'sonner'
import { auth, db } from '../../../firebase'

export default function ProfilePage() {
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = React.useState(true)
  const [profile, setProfile] = React.useState({
    fullName: '',
    email: '',
    userType: '',
    userId: '',
  })
  const userCollectionName = 'users'

  function getInitials(name) {
    if (!name) return 'U'
    return name
      .trim()
      .split(' ')
      .filter(Boolean)
      .map((part) => part[0])
      .join('')
      .slice(0, 2)
      .toUpperCase()
  }

  async function fetchUserProfile(userEmail, userUid) {
    const usersRef = collection(db, userCollectionName)
    if (userEmail) {
      const usersQuery = query(usersRef, where('email', '==', userEmail))
      const snapshot = await getDocs(usersQuery)
      if (!snapshot.empty) {
        const docSnapshot = snapshot.docs[0]
        return { id: docSnapshot.id, ...docSnapshot.data() }
      }
    }
    if (userUid) {
      const usersQuery = query(usersRef, where('userId', '==', userUid))
      const snapshot = await getDocs(usersQuery)
      if (!snapshot.empty) {
        const docSnapshot = snapshot.docs[0]
        return { id: docSnapshot.id, ...docSnapshot.data() }
      }
    }
    return null
  }

  React.useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        navigate('/login')
        return
      }
      setIsLoading(true)
      try {
        const userProfile = await fetchUserProfile(user.email, user.uid)
        if (userProfile) {
          setProfile({
            fullName: userProfile.fullName || user.displayName || 'User',
            email: userProfile.email || user.email || '',
            userType: userProfile.userType || '',
            userId: userProfile.userId || user.uid || '',
          })
        } else {
          setProfile({
            fullName: user.displayName || 'User',
            email: user.email || '',
            userType: '',
            userId: user.uid || '',
          })
        }
      } catch {
        toast.error('Unable to load profile details.')
      } finally {
        setIsLoading(false)
      }
    })

    return () => unsubscribe()
  }, [navigate])

  async function handleSignOut() {
    try {
      await signOut(auth)
      toast.success('Signed out successfully.')
      navigate('/login')
    } catch {
      toast.error('Unable to sign out. Please try again.')
    }
  }

  return (
    <div className="flex flex-col gap-6 p-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground font-serif tracking-tight">
          Profile
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          View your account details and sign out securely.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[2fr_1fr]">
        <Card className="border border-border">
          <CardHeader>
            <CardTitle className="text-sm font-semibold text-foreground">Account Details</CardTitle>
            <CardDescription className="text-xs">
              Information synced from Firebase Authentication and Firestore
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-5">
            <div className="flex items-center gap-4">
              <Avatar className="size-12">
                <AvatarFallback className="bg-primary text-primary-foreground text-sm font-semibold">
                  {getInitials(profile.fullName)}
                </AvatarFallback>
              </Avatar>
              <div className="flex flex-col">
                <span className="text-sm font-semibold text-foreground">
                  {profile.fullName || (isLoading ? 'Loading...' : 'User')}
                </span>
                <span className="text-xs text-muted-foreground">
                  {profile.email || (isLoading ? 'Loading...' : '')}
                </span>
              </div>
              {profile.userType ? (
                <Badge className="ml-auto text-[10px]" variant="secondary">
                  {profile.userType}
                </Badge>
              ) : null}
            </div>

            <Separator />

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="flex flex-col gap-2">
                <label className="text-xs font-medium text-foreground">Full name</label>
                <Input value={profile.fullName || ''} readOnly className="h-9 bg-muted/40" />
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-xs font-medium text-foreground">Email address</label>
                <Input value={profile.email || ''} readOnly className="h-9 bg-muted/40" />
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-xs font-medium text-foreground">User ID</label>
                <Input value={profile.userId || ''} readOnly className="h-9 bg-muted/40" />
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-xs font-medium text-foreground">Role</label>
                <Input value={profile.userType || ''} readOnly className="h-9 bg-muted/40" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-border">
          <CardHeader>
            <CardTitle className="text-sm font-semibold text-foreground">Session</CardTitle>
            <CardDescription className="text-xs">
              Manage your current login session.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <div className="rounded-lg border border-border p-4">
              <p className="text-xs text-muted-foreground">
                Signing out will end your current session on this device.
              </p>
            </div>
            <Button
              className="w-full bg-destructive text-white hover:bg-destructive/90"
              onClick={handleSignOut}
            >
              Sign out
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
